import { Component, OnInit } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import jsPDF from "jspdf";
import Swal from "sweetalert2";
import * as bootstrap from "bootstrap";

import { CartasService } from "../../../../shared/services/cartas.service";
import { VehiclesService } from "../../../../shared/services/vehicles.service";
import { gpcGoogleService } from "../../../../shared/services/gpcGoogle.service";
import { AdministracionService } from "../../../../shared/services/administracion.services";
import { DataService } from "../../../../shared/services/municipios.service";
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AdminSeafLoadingService } from "../../../../shared/services/adminseaf-loading.service";

// ============================================================
// Constantes
// ============================================================
/** Cartas que piden nombre y ciudad de la empresa */
const CARTAS_CON_EMPRESA = [1, 6, 7];
/** Carta que pide porcentaje y valor, y no muestra la tabla del vehículo */
const CARTA_PARTICIPACION = 7;

const COLOR_PRIMARIO: [number, number, number] = [64, 64, 64];
const COLOR_FONDO_SUAVE: [number, number, number] = [243, 243, 243];
const COLOR_TITULO: [number, number, number] = [0, 0, 0];
const COLOR_TEXTO: [number, number, number] = [33, 37, 41];
const COLOR_BORDE_TABLA: [number, number, number] = [190, 198, 212];

const MARGEN_IZQ = 20;
const MARGEN_INFERIOR = 22;
const LINE_HEIGHT = 5.6;

// Rutas de las imágenes en el bucket
const RUTA_SUPERTRANSPORTE = "ministerio/supertransporte.png";
const rutaLogo = (nit: string | number): string => `${nit}/logos/logo.png`;
const rutaFirma = (nit: string | number): string => `${nit}/logos/firma.png`;

// ============================================================
// Utilidades
// ============================================================
/** Ordena alfabéticamente (español, sin distinguir tildes ni mayúsculas) por el texto que devuelva `clave` */
const ordenarAlfabetico = <T>(items: T[], clave: (item: T) => unknown): T[] =>
  [...items].sort((a, b) =>
    String(clave(a) ?? "").localeCompare(String(clave(b) ?? ""), "es", {
      sensitivity: "base",
    })
  );

// ============================================================
// Tipos
// ============================================================
interface ImagenPdf {
  path: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Bloque de texto ya procesado por el backend (marcadores #n reemplazados) */
interface BloqueCarta {
  id: number;
  bloque: number;
  tipo: string;
  descripcion: string;
}

/** Estructura que arma getDatosVehiculoCarta */
interface DocumentoCarta {
  titulo: BloqueCarta | null;
  fecha: BloqueCarta | null;
  destino: BloqueCarta | null;
  saludo: BloqueCarta | null;
  cuerpo: BloqueCarta | null;
  extras: BloqueCarta[];
  cierre: BloqueCarta | null;
}

interface OpcionesCartaPdf {
  /** Respuesta de getDatosVehiculoCarta (documento, vehiculo, administracion) */
  resp: any;
  /** Posición, tamaño y ruta del logo de la empresa */
  logo: ImagenPdf;
  /** true = no se dibuja la tabla de datos del vehículo (carta 7) */
  omitirTablaVehiculo: boolean;
}

interface PdfContext {
  doc: jsPDF;
  y: number;
  marginLeft: number;
  usableWidth: number;
  pageHeight: number;
}

// ---------- Tabla de datos del vehículo ----------
/** Una celda "TÍTULO: valor". `peso` reparte el ancho de la fila (por defecto 1) */
interface CeldaTabla {
  titulo: string;
  valor: string;
  peso?: number;
}

interface SeccionTabla {
  titulo: string;
  filas: CeldaTabla[][];
}

interface CeldaMedida extends CeldaTabla {
  x: number;
  ancho: number;
  anchoValor: number;
  lineas: string[];
}

interface FilaMedida {
  celdas: CeldaMedida[];
  altura: number;
}

@Component({
  selector: "app-cartas",
  templateUrl: "./cartas.component.html",
  styleUrls: ["./cartas.component.scss"],
})
export class CartasComponent implements OnInit {
  // ---------- Catálogos ----------
  Cartas: any[] = [];
  Placas: any[] = [];
  ciudad: any[] = [];
  administraciones: any;

  // ---------- Formulario ----------
  cartaSeleccionada: any;
  placaSeleccionada: any;
  nombreEmpresa = "";
  ciudadEmpresa = "";
  porcentajeEmpresa = "";
  valorEmpresa = "";

  // ---------- Logo de la empresa (viene de la administración) ----------
  altura = 0;
  ancho = 0;
  posicionX = 0;
  posicionY = 0;

  // ---------- Vista previa ----------
  previewFile: any = null;

  constructor(
    private cartasService: CartasService,
    private vehiculosService: VehiclesService,
    private dataService: DataService,
    private sanitizer: DomSanitizer,
    private gpcGoogle: gpcGoogleService,
    private administracionesServices: AdministracionService,
    private loadingService: AdminSeafLoadingService,  
  ) {}

  // ============================================================
  // Ciclo de vida
  // ============================================================
 async ngOnInit(): Promise<void> {
   this.cargarDatosIniciales();
}
    //SEPARACION DE METODOS

   private cargarDatosIniciales(): void {
  forkJoin({
    cartas: this.cartasService.getAllCartas().pipe(
      catchError((error) => {
        console.error('Error al obtener cartas:', error);
        return of(null);
      })
    ),

    placas: this.vehiculosService.getPlacasAll().pipe(
      catchError((error) => {
        console.error('Error al obtener placas:', error);
        return of(null);
      })
    ),

    ciudad: this.dataService.getDatos().pipe(
      catchError((error) => {
        console.error('Error al obtener ciudad:', error);
        return of(null);
      })
    ),

    administraciones: this.administracionesServices.obtenerTodas().pipe(
      catchError((error) => {
        console.error('Error al cargar la administración:', error);
        return of(null);
      })
    )
  }).subscribe({
    next: (resultado) => {
      // Cartas
      if (resultado.cartas !== null) {
        this.Cartas = ordenarAlfabetico(
          resultado.cartas,
          (c) => c.descripcion
        );
      }

      // Placas
      if (resultado.placas !== null) {
        this.Placas = ordenarAlfabetico<any>(
          resultado.placas,
          (p) => p?.placa ?? p
        );
      }

      // Ciudad
      if (resultado.ciudad !== null) {
        this.ciudad = resultado.ciudad;
      }

      // Administración
      if (resultado.administraciones !== null) {
        this.configurarAdministracion(resultado.administraciones);
      }
    },
    error: (error) => {
      console.error('Error inesperado al inicializar el componente:', error);
    }
  });
} 


private configurarAdministracion(data: any[]): void {
  this.administraciones = data;

  const dimensionLogo = data?.[0]?.dimensionLogo;

  if (!dimensionLogo) {
    console.warn('La administración no tiene dimensiones de logo configuradas.');
    return;
  }

  this.altura = dimensionLogo.h;
  this.ancho = dimensionLogo.w;
  this.posicionX = dimensionLogo.cx;
  this.posicionY = dimensionLogo.cy;
}

  // ============================================================
  // Getters usados por la plantilla
  // ============================================================
  get requiereEmpresa(): boolean {
    return CARTAS_CON_EMPRESA.includes(Number(this.cartaSeleccionada));
  }

  get esCartaParticipacion(): boolean {
    return Number(this.cartaSeleccionada) === CARTA_PARTICIPACION;
  }

  get formularioInvalido(): boolean {
    if (!this.cartaSeleccionada || !this.placaSeleccionada) return true;
    if (this.requiereEmpresa && (!this.nombreEmpresa || !this.ciudadEmpresa)) return true;
    if (this.esCartaParticipacion && (!this.porcentajeEmpresa || !this.valorEmpresa)) return true;
    return false;
  }

  // ============================================================
  // Carga de datos
  // ============================================================
  private loadCartas(): void {
    this.cartasService.getAllCartas().subscribe({
      next: (data) => (this.Cartas = ordenarAlfabetico(data, (c) => c.descripcion)),
      error: (err) => console.error("Error al obtener cartas:", err.message),
    });
  }

  private cargarPlacas(): void {
    this.vehiculosService.getPlacasAll().subscribe({
      next: (data) => (this.Placas = ordenarAlfabetico<any>(data, (p) => p?.placa ?? p)),
      error: (err) => console.error("Error al obtener placas:", err.message),
    });
  }

  private cargarCiudad(): void {
    this.dataService.getDatos().subscribe({
      next: (data) => (this.ciudad = data),
      error: (err) => console.error("Error al obtener ciudad:", err.message),
    });
  }

  private cargarDataAdmin(): void {
    this.administracionesServices.obtenerTodas().subscribe({
      next: (data) => {
        this.administraciones = data;
        const dim = data[0].dimensionLogo;
        this.altura = dim.h;
        this.ancho = dim.w;
        this.posicionX = dim.cx;
        this.posicionY = dim.cy;
      },
      error: (err) => console.error("Error al cargar la administración:", err),
    });
  }

  /** NIT de la empresa: el de la administración cargada, o el que trae la respuesta del PDF */
  private nitEmpresa(administracion?: any): string | undefined {
    return this.administraciones?.[0]?.nit ?? administracion?.nit;
  }

  // ============================================================
  // Vista previa (modal)
  // ============================================================
  openPreview(file: any): void {
    // Liberar el blob de la vista previa anterior
    if (this.previewFile?.url) {
      URL.revokeObjectURL(this.previewFile.url);
    }
    this.previewFile = file;

    const modalEl = document.getElementById("filePreviewModal");
    if (modalEl) {
      new bootstrap.Modal(modalEl).show();
    }
  }

  /** Nombre del PDF: "<TIPO DE CARTA> - <PLACA>.pdf", sin caracteres no válidos */
  private nombreArchivo(): string {
    const carta = this.Cartas.find((c) => c.id === this.cartaSeleccionada);
    const limpiar = (texto: string) =>
      texto.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, " ").trim();
    const partes = [carta?.descripcion, this.placaSeleccionada]
      .filter(Boolean)
      .map((p) => limpiar(String(p)));
    return `${partes.join(" - ") || "Carta"}.pdf`;
  }

  descargarPreview(): void {
    if (!this.previewFile?.url) return;
    const enlace = document.createElement("a");
    enlace.href = this.previewFile.url;
    enlace.download = this.previewFile.name;
    enlace.click();
  }

  sanitizeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  // ============================================================
  // Flujo principal: validar → pedir datos → generar PDF → vista previa
  // ============================================================
  exportarPDF(): void {
    if (!this.validarFormulario()) return;
    this.loadingService.mostrar(
      this.Cartas.find((c) => c.id === this.cartaSeleccionada)?.descripcion,
     
      [],
       "Generando PDF...",

    );
    this.cartasService.getDatosVehiculoCarta(this.construirPayload()).subscribe({
      next: async (resp) => {
        if (!resp) {
          Swal.fire("Información", "No se encontraron datos", "info");
          return;
        }
        await this.generarPdf(resp);
      },
      error: (err) => {
        console.error("Error:", err.message);
        Swal.fire("Error", "No se pudo generar el PDF", "error");
      },
    });
  }

  private async generarPdf(resp: any): Promise<void> {
    try {
      const nit = this.nitEmpresa(resp.administracion);

      const blob = await this.construirPdf({
        resp,
        logo: this.logoEnEncabezado(nit ? rutaLogo(nit) : ""),
        omitirTablaVehiculo: this.esCartaParticipacion,
      });

      this.loadingService.ocultar();
      this.openPreview({
        name: this.nombreArchivo(),
        type: "pdf",
        url: URL.createObjectURL(blob),
      });
    } catch (err: any) {
      console.error("Error al generar el PDF:", err?.message ?? err);
      Swal.fire("Error", "No se pudo generar el PDF", "error");
    }
  }

  private validarFormulario(): boolean {
    const error = (msg: string): false => {
      Swal.fire("Error", msg, "error");
      return false;
    };

    if (!this.cartaSeleccionada) return error("Debe seleccionar una carta");
    if (!this.placaSeleccionada) return error("Debe seleccionar una placa");

    if (this.requiereEmpresa) {
      if (!this.nombreEmpresa?.trim()) return error("Debe ingresar el nombre de la empresa");
      if (!this.ciudadEmpresa?.trim()) return error("Debe seleccionar la ciudad de la empresa");
    }

    if (this.esCartaParticipacion) {
      if (this.porcentajeEmpresa == null || Number(this.porcentajeEmpresa) <= 0) {
        return error("Debe ingresar un porcentaje válido");
      }
      if (this.valorEmpresa == null || Number(this.valorEmpresa) <= 0) {
        return error("Debe ingresar un valor válido");
      }
    }
    return true;
  }

  private construirPayload() {
    const conEmpresa = this.requiereEmpresa;
    const conParticipacion = this.esCartaParticipacion;
    return {
      placa: this.placaSeleccionada,
      id: this.cartaSeleccionada,
      nombreEmpresa: conEmpresa ? this.nombreEmpresa : null,
      ciudadEmpresa: conEmpresa ? this.ciudadEmpresa : null,
      porcentajeEmpresa: conParticipacion ? this.porcentajeEmpresa : null,
      valorEmpresa: conParticipacion ? this.valorEmpresa : null,
    };
  }

  // ============================================================
  // Construcción del PDF
  // ============================================================
  private async construirPdf(opciones: OpcionesCartaPdf): Promise<Blob> {
    const { resp, logo, omitirTablaVehiculo } = opciones;

    const documento: DocumentoCarta | undefined = resp.documento;
    if (!documento) {
      throw new Error(
        "La respuesta no incluye 'documento'. Actualiza getDatosVehiculoCarta en el backend."
      );
    }

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "letter",
      compress: true,
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const ctx: PdfContext = {
      doc,
      y: 20,
      marginLeft: MARGEN_IZQ,
      usableWidth: pageWidth - MARGEN_IZQ * 2,
      pageHeight: doc.internal.pageSize.getHeight(),
    };

    // Encabezado
    this.dibujarEncabezado(doc, documento.titulo?.descripcion ?? "");
    await this.dibujarImagenes(doc, {
      supertransporte: { path: RUTA_SUPERTRANSPORTE, x: 168, y: 7, w: 35, h: 12 },
      logo,
    });
    ctx.y += 20;

    // Cuerpo
    this.imprimirBloque(ctx, documento.fecha, 6);
    this.imprimirBloque(ctx, documento.destino, 6, true);
    this.imprimirBloque(ctx, documento.saludo, 4);
    this.imprimirBloque(ctx, documento.cuerpo, 6, false, true);

    if (!omitirTablaVehiculo) {
      this.dibujarTablaVehiculo(ctx, resp.vehiculo);
    }

    (documento.extras ?? []).forEach((b) => this.imprimirBloque(ctx, b, 6));
    this.imprimirBloque(ctx, documento.cierre, 6);

    // Firma y pie de página
    await this.dibujarFirma(ctx, resp.administracion);
    this.aplicarFooters(doc);

    return doc.output("blob");
  }

  // ---------- Imágenes del bucket ----------
  /** Descarga una imagen del bucket y la devuelve en base64 (null si no existe o falla) */
  private async cargarImagenBase64(path: string): Promise<string | null> {
    if (!path) return null;
    try {
      const res = await this.gpcGoogle.getSignedUrl(path).toPromise();
      if (!res?.url) return null;
      return (await this.gpcGoogle.getImageBase64(res.url)) || null;
    } catch {
      return null;
    }
  }

  private async dibujarImagenes(
    doc: jsPDF,
    imageMap: Record<string, ImagenPdf>
  ): Promise<void> {
    const imagenes = Object.values(imageMap);
    const base64Images = await Promise.all(
      imagenes.map((img) => this.cargarImagenBase64(img.path))
    );

    base64Images.forEach((base64, idx) => {
      const { x, y, w, h } = imagenes[idx];
      if (base64) {
        doc.addImage(base64, "JPG", x, y, w, h, undefined, "FAST");
      } else {
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text("SIN SOPORTE CARGADO", x + 2, y + h / 2, { maxWidth: w });
      }
    });
  }

  // ---------- Encabezado ----------
  /** Ajusta el logo (manteniendo su proporción) al recuadro izquierdo del encabezado */
  private logoEnEncabezado(path: string): ImagenPdf {
    const maxW = 48;
    const maxH = 8;
    const proporcion = this.ancho > 0 && this.altura > 0 ? this.ancho / this.altura : 3;
    const w = Math.min(maxW, maxH * proporcion);
    const h = w / proporcion;
    return { path, x: 14, y: 3 + (20 - h) / 2, w, h };
  }
  private dibujarEncabezado(doc: jsPDF, title: string): void {
    const pageWidth = 210;
    const marginLeft = 10;
    const marginRight = 5;
    const rectY = 3;
    const headerHeight = 20;
    const rectWidth = pageWidth - marginLeft - marginRight;

    doc.setFillColor(255, 255, 255);
    doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(...COLOR_TITULO);

    const lines = doc.splitTextToSize(title.trim(), rectWidth - 110);
    const centerX = marginLeft + rectWidth / 2;
    const lineH = 6;
    const textY = rectY + (headerHeight - lines.length * lineH) / 2 + 4.5;

    doc.text(lines, centerX, textY, { align: "center", lineHeightFactor: 1.2 });
  }

  // ---------- Cuerpo ----------
  private imprimirBloque(
    ctx: PdfContext,
    bloque: BloqueCarta | null | undefined,
    gap: number,
    bold = false,
    justify = false
  ): void {
    if (!bloque) return;
    const { doc, marginLeft, usableWidth, pageHeight } = ctx;

    const txt = String(bloque.descripcion || "").replace(/\r/g, "");
    doc.setFont("helvetica", bold ? "bold" : "normal").setFontSize(11).setTextColor(...COLOR_TEXTO);

    const lines: string[] = doc.splitTextToSize(txt, usableWidth);
    if (ctx.y + lines.length * LINE_HEIGHT > pageHeight - MARGEN_INFERIOR) {
      doc.addPage();
      ctx.y = 25;
    }

    if (justify) {
      // Se justifican solo párrafos de una línea lógica; la última línea queda a la izquierda
      lines.forEach((l, i) => {
        const esUltima = i === lines.length - 1;
        const opciones =
          esUltima || txt.includes("\n")
            ? undefined
            : { maxWidth: usableWidth, align: "justify" as const };
        doc.text(l, marginLeft, ctx.y + i * LINE_HEIGHT, opciones);
      });
    } else {
      doc.text(lines, marginLeft, ctx.y, { lineHeightFactor: 1.3 });
    }
    ctx.y += lines.length * LINE_HEIGHT + gap;
  }

  // ---------- Tabla de datos del vehículo ----------
  /** Agrupa los datos en secciones: cada fila tiene una o dos celdas */
  private seccionesVehiculo(v: any): SeccionTabla[] {
    const dato = (valor: any): string => String(valor || "N/A");

    const propietario = v.propietario
      ? [
          v.propietario.primernombre,
          v.propietario.segundonombre,
          v.propietario.papellidos,
          v.propietario.seapellidos,
        ]
          .filter(Boolean)
          .join(" ")
      : "";

    return [
      {
        titulo: "DATOS DEL VEHÍCULO",
        filas: [
          [
            { titulo: "PLACA:", valor: dato(v.placa) },
            { titulo: "CLASE:", valor: dato(v.tipoVehiculo?.tipos) },
          ],
          [
            { titulo: "MARCA:", valor: dato(v.marcas?.marca) },
            { titulo: "MODELO:", valor: dato(v.modelo) },
          ],
          [
            { titulo: "MOTOR:", valor: dato(v.motor) },
            { titulo: "CHASIS:", valor: dato(v.chasis) },
          ],
          [
            { titulo: "CAPACIDAD:", valor: dato(v.capacidad) },
            { titulo: "COMBUSTIBLE:", valor: dato(v.tipoCombustible?.nombre) },
          ],
          [{ titulo: "SERVICIO:", valor: dato(v.tipoRelacion?.relacion) }],
        ],
      },
      {
        titulo: "DATOS DEL PROPIETARIO",
        filas: [
          [
            { titulo: "PROPIETARIO:", valor: dato(propietario.toUpperCase()), peso: 2 },
            { titulo: "CC/NIT:", valor: dato(v.Idpropietario), peso: 1 },
          ],
        ],
      },
    ];
  }

  /** Calcula el ancho de cada celda y el alto de la fila según el texto que ocupa */
  private medirFila(
    doc: jsPDF,
    fila: CeldaTabla[],
    usableWidth: number,
    gapCol: number,
    titleWidth: number
  ): FilaMedida {
    doc.setFont("helvetica", "normal").setFontSize(9);

    const totalPeso = fila.reduce((suma, c) => suma + (c.peso ?? 1), 0);
    const disponible = usableWidth - gapCol * (fila.length - 1);
    let x = 0;

    const celdas = fila.map((c) => {
      const ancho = (disponible * (c.peso ?? 1)) / totalPeso;
      const anchoValor = ancho - titleWidth;
      const lineas: string[] = doc.splitTextToSize(c.valor, anchoValor - 3);
      const celda: CeldaMedida = { ...c, x, ancho, anchoValor, lineas };
      x += ancho + gapCol;
      return celda;
    });

    const maxLineas = Math.max(...celdas.map((c) => c.lineas.length));
    return { celdas, altura: Math.max(8, maxLineas * 4 + 4) };
  }

  private dibujarTablaVehiculo(ctx: PdfContext, vehiculo: any): void {
    const { doc, marginLeft, usableWidth, pageHeight } = ctx;

    const gapCol = 4;
    const titleWidth = 27;
    const alturaBarra = 6.5;
    const gapSeccion = 3;

    // 1) Medir todo antes de dibujar, para saltar de página si no cabe completo
    const secciones = this.seccionesVehiculo(vehiculo).map((seccion) => ({
      titulo: seccion.titulo,
      filas: seccion.filas.map((fila) =>
        this.medirFila(doc, fila, usableWidth, gapCol, titleWidth)
      ),
    }));

    const alturaTotal =
      secciones.reduce(
        (suma, s) => suma + alturaBarra + s.filas.reduce((h, f) => h + f.altura, 0),
        0
      ) +
      gapSeccion * (secciones.length - 1);

    if (ctx.y + alturaTotal > pageHeight - MARGEN_INFERIOR) {
      doc.addPage();
      ctx.y = 25;
    }

    // 2) Dibujar
    let y = ctx.y;

    secciones.forEach((seccion, idx) => {
      if (idx > 0) y += gapSeccion;

      // Barra de título de la sección
      doc.setFillColor(...COLOR_PRIMARIO).rect(marginLeft, y, usableWidth, alturaBarra, "F");
      doc.setFont("helvetica", "bold").setFontSize(8.5).setTextColor(255, 255, 255);
      doc.text(seccion.titulo, marginLeft + 2, y + 4.5);
      y += alturaBarra;

      // Filas: título en celda suave, valor en celda blanca
      seccion.filas.forEach((fila) => {
        fila.celdas.forEach((celda) => {
          const x = marginLeft + celda.x;

          doc.setDrawColor(...COLOR_BORDE_TABLA).setLineWidth(0.2);
          doc.setFillColor(...COLOR_FONDO_SUAVE).rect(x, y, titleWidth, fila.altura, "FD");
          doc.setFillColor(255, 255, 255).rect(x + titleWidth, y, celda.anchoValor, fila.altura, "FD");

          doc.setFont("helvetica", "bold").setFontSize(8).setTextColor(...COLOR_TITULO);
          doc.text(celda.titulo, x + 2, y + 5.2);

          doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...COLOR_TEXTO);
          doc.text(celda.lineas, x + titleWidth + 2, y + 5.2, { lineHeightFactor: 1.15 });
        });
        y += fila.altura;
      });
    });

    ctx.y = y + 10;

    // Restaurar colores después de la tabla
    doc.setFillColor(255, 255, 255);
    doc.setTextColor(0);
  }

  // ---------- Firma y pie de página ----------
  private async dibujarFirma(ctx: PdfContext, administracion: any): Promise<void> {
    const { doc, marginLeft, pageHeight } = ctx;

    if (ctx.y + 28 > pageHeight - 18) {
      doc.addPage();
      ctx.y = 30;
    }

    const yFirma = ctx.y + 12;
    const lineWidth = 80;
    const nit = this.nitEmpresa(administracion);

    // Imagen de la firma (opcional): se apoya sobre la línea
    if (nit) {
      const firma = await this.cargarImagenBase64(rutaFirma(nit));
      if (firma) {
        doc.addImage(firma, "PNG", marginLeft + 5, yFirma - 15, 55, 15);
      } else {
        console.warn(`No se pudo cargar la firma para el NIT ${nit}.`);
      }
    }

    doc.setDrawColor(...COLOR_PRIMARIO).setLineWidth(0.4);
    doc.line(marginLeft, yFirma, marginLeft + lineWidth, yFirma);

    doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(...COLOR_TITULO);
    doc.text(`${administracion?.representanteLegal ?? ""}`, marginLeft, yFirma + 5);

    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(90);
    doc.text("Representante Legal", marginLeft, yFirma + 10);
    doc.text(
      `${administracion?.empresa ?? ""}  NIT. ${administracion?.nit ?? ""}`,
      marginLeft,
      yFirma + 15
    );
  }

  private aplicarFooters(doc: jsPDF): void {
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      this.dibujarFooter(doc, i, totalPages);
    }
  }

  private dibujarFooter(doc: jsPDF, currentPage: number, totalPages: number): void {
    const pageHeight = doc.internal.pageSize.height;
    const now = new Date();
    const fecha = now.toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" });
    const hora = now.toLocaleTimeString("es-CO", { hour: "numeric", minute: "2-digit", hour12: true });

    doc.setDrawColor(...COLOR_PRIMARIO).setLineWidth(0.3);
    doc.line(10, pageHeight - 16, 205, pageHeight - 16);

    doc.setFont("helvetica", "italic").setFontSize(8).setTextColor(100);
    doc.textWithLink("Generado desde Admin SEAF – INDESOFT SAS", 110, pageHeight - 12, {
      url: "https://indesoftsas.com",
      align: "center",
    });

    doc.text(`${fecha} - ${hora}`, 10, pageHeight - 5);
    doc.text(`Página ${currentPage} de ${totalPages}`, 205, pageHeight - 5, { align: "right" });
  }
}